import _ from 'underscore';

import { getI9nSchemaNameBySystem } from '@biteinc/common';
import type { I9nSchema } from '@biteinc/common';
import { OrderChannel } from '@biteinc/enums';
import type { ModelType } from '@biteinc/enums';

import { TimeHelper } from './time_helper';

export function getArchivedBadgeHtml(
  archivedAt: number | undefined,
  isModGroup: boolean,
): string | null {
  if (!archivedAt) {
    return null;
  }

  let alt = `Archived on ${TimeHelper.format(archivedAt)}.`;
  if (isModGroup) {
    alt += ' Will be removed a week after that.';
  } else {
    alt += ' Will be kept indefinitely.';
  }
  return `<span class="cell-badge archived" title="${alt}">archived</span>`;
}

export function getBadgeHtml(i9nSchema: I9nSchema, modelType?: ModelType): string {
  const title = `${getI9nSchemaNameBySystem(i9nSchema.system)}${modelType ? ` ${modelType}` : ''}`;
  return `<img class="badge-img" src="${i9nSchema.iconUrl}" title="${title}"/>`;
}

export function i9nBadgesHtml(i9nSchemas: I9nSchema[], delimiter: string = '&nbsp;'): string {
  return _.chain(i9nSchemas)
    .uniq()
    .sortBy(({ type: i9nSchemaType, name }) => {
      switch (i9nSchemaType) {
        case 'pos':
          return `0-${name}`;
        case 'supplementary-menu-source':
          return `1-${name}`;
        case 'fulfillment':
          return `2-${name}`;
        case 'loyalty':
          return `3-${name}`;
        case 'loyalty-earner':
          return `4-${name}`;
        case 'comp-card':
          return `5-${name}`;
        case 'campus-card':
          return `6-${name}`;
        case 'stored-value':
          return `7-${name}`;
        case 'ecomm-payment':
          return `8-${name}`;
        case 'kiosk-payment':
          return `9-${name}`;
        case 'cash-machine':
          return `a-${name}`;
        case 'table-tracker':
          return `b-${name}`;
        case 'marketing':
          return `c-${name}`;
        case 'sms-notifier':
          return `d-${name}`;
        case 'pos-data-source':
          return `e-${name}`;
        default:
          i9nSchemaType satisfies never;
          throw new Error(`Unknown I9n schema type: ${i9nSchemaType}`);
      }
    })
    .map((i9nSchema) => getBadgeHtml(i9nSchema))
    .value()
    .join(delimiter);
}

function tagCssClassFromOrderChannel(orderChannel: OrderChannel): string {
  switch (orderChannel) {
    case OrderChannel.DriveThru:
      return 'drive-thru';
    case OrderChannel.Flash:
      return 'contactless';
    case OrderChannel.Kiosk:
      return 'kiosk';
    case OrderChannel.Linebuster:
      return 'linebuster';
    case OrderChannel.Web:
      return 'web';
  }
}

export function buildOrderChannelTag(
  orderChannel: OrderChannel,
  channelName: string,
  options: {
    sessionUserIsBite: boolean;
    isLiveLocation: boolean;
    isCanary: boolean;
    isGarconCanary: boolean;
    includeArrowIcon?: boolean;
  },
): string {
  let cssClass = tagCssClassFromOrderChannel(orderChannel);
  if (options.isLiveLocation) {
    cssClass += ' live';
  }
  const canaryDesignation = options.sessionUserIsBite && options.isCanary ? '✪ ' : '';
  const garconCanaryDesignation = options.sessionUserIsBite && options.isGarconCanary ? 'Ⓖ ' : '';
  const arrowIcon = options.includeArrowIcon ? ' ⇨' : '';
  return `<span class="order-channel-tag ${cssClass}">${canaryDesignation}${garconCanaryDesignation}${channelName}${arrowIcon}</span>`;
}
